import { computed, ref, watch, } from 'vue';
import vuexStore from '@/vuex-store';
import { afterDays, beforeDays, } from '@/utils/time';
import { makeRangePickerDate } from '@/utils';
import { i18n } from '@/locales';
import { format } from 'date-fns';
import { operationSettingHistoryActionType } from '@/vuex-store/operation-setting-history/actions';
import { settingTypeOptionList } from '@/constants/operationSetting/operationSettingHistory';
import { operationSettingHistoryGetterType } from '@/vuex-store/operation-setting-history/getters';
import dateFormatType from '@/constants/dateFormatType';
/**
 * Composable: 운행설정 이력
 */
export const useOperationHistory = () => {
    const lc = 'views.operation_setting_history.';
    const dateFormat = dateFormatType.DATE;
    const firstPageNumber = 1;
    const locales = {
        selectedPeriod: i18n.t(`${lc}selected_period`),
        selectedHub: i18n.t(`${lc}selected_hub`),
        selectedStore: i18n.t(`${lc}selected_store`),
        selectedType: i18n.t(`${lc}selected_type`),
        reset: i18n.t(`${lc}reset`),
    };
    /** locale */
    const t = (key, ...args) => i18n.t(key, args);
    /** 여러 filterOption 수정에 따른 다중호출 방지 */
    const isFilterReset = ref(true);
    /** 조회 기간, 선택 날짜 */
    const pickedDate = ref([]);
    /** 오늘 */
    const today = ref(new Date());
    /** 최소 선택 가능일 */
    const minDate = ref('');
    /** 최대 선택 가능일 */
    const maxDate = ref(format(today.value, dateFormat));
    /** 설정 유형 */
    const selectedType = ref(settingTypeOptionList[0].value);
    /** 조회 허브 */
    const selectedHub = ref('');
    /** 조회 상점 */
    const selectedStore = ref('');
    /** view에 노출되는 선택 날짜 텍스트 */
    const periodText = computed(() => vuexStore.getters[operationSettingHistoryGetterType.getterBetweenDate].join(' ~ '));
    /** 상세이력 다이얼로그 */
    const isDialogOpen = computed(() => vuexStore.state.operationSettingHistory.isDialogOpen);
    /** 타 허브 이력 조회 권한 */
    const isPermittedUser = computed(() => {
        const isPermissionOtherHub = vuexStore.getters['user/isPermissionOtherHub'];
        const isHead = vuexStore.getters['user/isHead'];
        return isPermissionOtherHub || isHead;
    });
    /** 허브 리스트 */
    const computedHubList = computed(() => vuexStore.getters[operationSettingHistoryGetterType.getterHubOptionList]);
    /** 상점 리스트 */
    const computedStoreList = computed(() => vuexStore.getters[operationSettingHistoryGetterType.getterStoreOptionList]);
    /**
   * 상세내역 조회
   */
    const getOperationSettingHistoryList = async () => {
        try {
            vuexStore.dispatch(operationSettingHistoryActionType.setIsGettingHistoryOnError, false);
            vuexStore.dispatch(operationSettingHistoryActionType.setIsGettingHistoryOnFetching, true);
            await vuexStore.dispatch(operationSettingHistoryActionType.getOperationSettingHistoryList);
        }
        catch {
            vuexStore.dispatch(operationSettingHistoryActionType.setIsGettingHistoryOnError, true);
        }
        vuexStore.dispatch(operationSettingHistoryActionType.setIsGettingHistoryOnFetching, false);
    };
    /**
   * 필터 초기값으로 리셋
   */
    const initDate = async () => {
        isFilterReset.value = false;
        today.value = new Date();
        const startDate = format(beforeDays(today.value, 1), dateFormat);
        const endDate = format(afterDays(today.value, 0), dateFormat);
        const betweenDate = [
            startDate,
            endDate,
        ];
        pickedDate.value = betweenDate;
        minDate.value = '';
        maxDate.value = format(today.value, dateFormat);
        // 설정 유형 초기화
        selectedType.value = 'ALL';
        await vuexStore.dispatch(operationSettingHistoryActionType.setFilterOption, {
            betweenDate,
            pageNumber: firstPageNumber,
        });
    };
    /**
   * 다이얼로그 설정 파기
   */
    const destroyFilter = async () => {
        // 시간 초기화
        initDate();
        // 허브 초기화
        const hubId = vuexStore.getters['user/hubId'];
        selectedHub.value = hubId;
        // 상점 초기화
        selectedStore.value = 'ALL';
    };
    /**
   * 초기화
   */
    const init = async () => {
        isFilterReset.value = true;
        // 권한이 없으면 본인 허브만 조회 가능합니다.
        if (!isPermittedUser.value) {
            vuexStore.dispatch(operationSettingHistoryActionType.getHubInfo);
        }
        else {
            vuexStore.dispatch(operationSettingHistoryActionType.getHubList);
        }
        // 기간 초기화
        await initDate();
        // 선택한 허브 설정
        const hubId = vuexStore.getters['user/hubId'];
        selectedHub.value = hubId;
        await vuexStore.dispatch(operationSettingHistoryActionType.setSelectedHub, hubId);
        // 상점 조회
        selectedStore.value = 'ALL';
        await vuexStore.dispatch(operationSettingHistoryActionType.setSelectedStore, 'ALL');
        vuexStore.dispatch(operationSettingHistoryActionType.getStoreList);
        // 설정 유형 초기화
        selectedType.value = 'ALL';
        await vuexStore.dispatch(operationSettingHistoryActionType.setFilterOption, {
            selectedType: 'ALL',
        });
        // 운행설정 리스트 조회
        getOperationSettingHistoryList();
    };
    /**
     * 시작일 데이터 피커
     */
    const handleStartRangeDatePicker = (startDate) => {
        const dateFormatStartDate = new Date(startDate);
        minDate.value = format(beforeDays(dateFormatStartDate, 6), dateFormat);
        const isMaxLaterThanToday = afterDays(dateFormatStartDate, 6) > today.value.getTime();
        // 처음 선택한 날로부터 계산된 7일 초과된 날이 오늘보다 이후일 경우 오늘날짜로 비활성화
        if (isMaxLaterThanToday) {
            maxDate.value = format(afterDays(today.value, 0), dateFormat);
            return;
        }
        maxDate.value = format(afterDays(dateFormatStartDate, 6), dateFormat);
    };
    /**
     * 종료일 데이터 피커
     */
    const handleEndRangeDatePicker = () => {
        maxDate.value = format(today.value, dateFormat);
        minDate.value = '';
    };
    /**
     * 피커 승인 버튼 핸들러
     */
    const handleClickPickerSubmit = () => {
        vuexStore.dispatch(operationSettingHistoryActionType.setFilterOption, {
            betweenDate: makeRangePickerDate(pickedDate.value),
            pageNumber: firstPageNumber,
        });
        getOperationSettingHistoryList();
    };
    /** 허브 변경 핸들러 */
    const handleUpdateSelectedHub = async (_value) => {
        const value = _value;
        if (!value) {
            return;
        }
        selectedHub.value = value;
        await vuexStore.dispatch(operationSettingHistoryActionType.setSelectedHub, value);
        selectedStore.value = 'ALL';
        await vuexStore.dispatch(operationSettingHistoryActionType.setSelectedStore, selectedStore.value);
        vuexStore.dispatch(operationSettingHistoryActionType.getStoreList);
        await vuexStore.dispatch(operationSettingHistoryActionType.setFilterOption, {
            pageNumber: firstPageNumber,
        });
        getOperationSettingHistoryList();
    };
    /** 허브 블러 핸들러 */
    const handleBlurHub = async (value) => {
        // 비어 있으면 본인 허브로 설정
        if (!value.target.value) {
            const hubId = await vuexStore.getters['user/hubId'];
            selectedHub.value = hubId;
            handleUpdateSelectedHub(hubId);
        }
    };
    /** 상점 변경 핸들러 */
    const handleUpdateSelectedStore = async (_value) => {
        const value = _value;
        if (!value) {
            return;
        }
        selectedStore.value = value;
        await vuexStore.dispatch(operationSettingHistoryActionType.setSelectedStore, value);
        vuexStore.dispatch(operationSettingHistoryActionType.getStoreList);
        await vuexStore.dispatch(operationSettingHistoryActionType.setFilterOption, {
            pageNumber: firstPageNumber,
        });
        getOperationSettingHistoryList();
    };
    /** 상점 블러 핸들러 */
    const handleBlurStore = async (value) => {
        // 비어 있으면 본인 허브로 설정
        if (!value.target.value) {
            const storeId = 'ALL';
            selectedStore.value = storeId;
            handleUpdateSelectedStore(storeId);
        }
    };
    /**
     * 설정 유형 핸들러
     */
    const handleChangeSearchFieldType = async (value) => {
        selectedType.value = value;
        await vuexStore.dispatch(operationSettingHistoryActionType.setFilterOption, {
            selectedType: value,
            pageNumber: firstPageNumber,
        });
        getOperationSettingHistoryList();
    };
    /** 리셋 버튼 클릭 핸들러 */
    const handleClickResetBtn = async () => {
        init();
    };
    watch(() => isDialogOpen.value, async (value) => {
        if (value) {
            return;
        }
        // 닫을 시
        await destroyFilter();
        vuexStore.dispatch(operationSettingHistoryActionType.clearList);
    });
    return {
        t,
        isPermittedUser,
        locales,
        minDate,
        maxDate,
        pickedDate,
        periodText,
        computedHubList,
        computedStoreList,
        selectedHub,
        selectedStore,
        selectedType,
        getOperationSettingHistoryList,
        handleChangeSearchFieldType,
        handleClickResetBtn,
        handleUpdateSelectedHub,
        handleUpdateSelectedStore,
        handleClickPickerSubmit,
        handleStartRangeDatePicker,
        handleEndRangeDatePicker,
        handleBlurHub,
        handleBlurStore,
        init,
    };
};
